import * as React from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Checkbox, TextField } from "@mui/material";
import { Check, ChevronDown, HardDriveUploadIcon, Plus } from "lucide-react";
import {
  useFetchBrandsQuery,
  useFetchCategoryTreeQuery,
} from "../../../../../services/baseQuery";
import {
  dummyProducts,
  dummyCategoryTree,
  transformCategories,
} from "../../../../../dummyData";

const ProductFilters = ({ onFilterChange, filters }) => {
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    onFilterChange?.(newFilters);
  };

  // Extract unique brands from dummy data
  const brands = React.useMemo(() => {
    const uniqueBrands = Array.from(
      new Set(dummyProducts.map(product => product.brand.name)),
    );
    return uniqueBrands.map(name => ({ _id: name, name }));
  }, []);

  // Transform categories for display
  const categories = React.useMemo(() => {
    return transformCategories(dummyCategoryTree);
  }, []);

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Brand Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Brand</InputLabel>
        <Select
          value={filters.brand}
          label="Brand"
          onChange={e => handleFilterChange("brand", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">All Brands</MenuItem>
          {brands.map(brand => (
            <MenuItem key={brand._id} value={brand.name}>
              {brand.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category Filter */}
      <FormControl size="small" sx={{ minWidth: 250 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={filters.category}
          label="Category"
          onChange={e => handleFilterChange("category", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">All Categories</MenuItem>
          {categories.map(category => (
            <MenuItem
              key={category.id}
              value={category.id}
              sx={{
                paddingLeft: category.parentId ? 4 : 2, // Indent child categories
                fontWeight: category.parentId ? "normal" : "bold", // Make parent categories bold
              }}>
              {category.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={filters.status}
          label="Status"
          onChange={e => handleFilterChange("status", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="in_stock">In Stock</MenuItem>
          <MenuItem value="out_of_stock">Out of Stock</MenuItem>
        </Select>
      </FormControl>

      {/* Sort By Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={filters.sortBy}
          label="Sort By"
          onChange={e => handleFilterChange("sortBy", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">None</MenuItem>
          <MenuItem value="name_asc">Name (A-Z)</MenuItem>
          <MenuItem value="name_desc">Name (Z-A)</MenuItem>
          <MenuItem value="price_asc">Price (Low-High)</MenuItem>
          <MenuItem value="price_desc">Price (High-Low)</MenuItem>
          <MenuItem value="date_desc">Newest First</MenuItem>
          <MenuItem value="date_asc">Oldest First</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export default ProductFilters;
